import { AdminApiError, adminApiUrl } from './adminApi';

/**
 * /api/admin/agent-prompts — the system prompt overrides (kind 'system'), moved
 * from the product's Settings -> Agent to this console. Superadmin only: the
 * API refuses every other console role. A prompt is keyed by (kind, key) and
 * applies to every model and every agent.
 */
export type AgentPromptKind = 'system' | 'window' | 'config';

export interface AgentPromptRow {
  id: string;
  kind: AgentPromptKind;
  key: string;
  title: string;
  content: string;
  enabled: boolean;
  date_updated: string;
}

/** The built-in (code) prompt per slot. A gateway from an older image may omit
 * `video_reproduction`. */
export interface PromptDefaults {
  orchestrator: string;
  architect: string;
  editor: string;
  video_reproduction?: string;
}

const BASE = '/api/admin/agent-prompts';

async function send<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const res = await fetch(adminApiUrl(path), {
    credentials: 'include',
    ...init,
    headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new AdminApiError(body.error ?? `${fallback}: ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

export function listPrompts(): Promise<AgentPromptRow[]> {
  return send(BASE, {}, 'prompts list failed');
}

/** POST is an upsert on (kind, key). No `model` field: api-py answers 400 to one. */
export function upsertPrompt(body: {
  kind: AgentPromptKind;
  key: string;
  title?: string;
  content?: string;
  enabled?: boolean;
}): Promise<AgentPromptRow> {
  return send(BASE, { method: 'POST', body: JSON.stringify(body) }, 'prompt save failed');
}

export function patchPrompt(
  id: string,
  body: Partial<Pick<AgentPromptRow, 'title' | 'content' | 'enabled' | 'key'>>,
): Promise<AgentPromptRow> {
  return send(
    `${BASE}/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    'prompt update failed',
  );
}

export function deletePrompt(id: string): Promise<{ ok: boolean }> {
  return send(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' }, 'prompt delete failed');
}

/** The built-in prompts, fetched by api-py from agent-gateway. 502 when the
 * gateway cannot answer. */
export function fetchPromptDefaults(): Promise<PromptDefaults> {
  return send(`${BASE}/defaults`, {}, 'prompt defaults failed');
}
